import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { followUpApi } from '../lib/api'
import type {
  CutChaseBatchRequest,
  FollowUpItemsParams,
  PrepareChaseRequest,
  SaveChaseSettingsRequest,
  SaveCampaignViewSettingsRequest,
  SaveFollowUpKindRequest,
  UpdateFollowUpItemRequest,
} from '../types/followUp'

export function useFollowUpKinds() {
  return useQuery({
    queryKey: ['followUp', 'kinds'],
    queryFn: () => followUpApi.getKinds().then(r => r.data),
  })
}

// id present => rename/mode change (PUT); absent => create (POST)
export function useSaveFollowUpKind() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: SaveFollowUpKindRequest & { id?: string }) =>
      id ? followUpApi.updateKind(id, data).then(() => undefined) : followUpApi.createKind(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followUp', 'kinds'] })
    },
  })
}

export function useDeleteFollowUpKind() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => followUpApi.deleteKind(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followUp', 'kinds'] })
    },
  })
}

export function useRestoreStarterQuestions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => followUpApi.restoreStarterQuestions().then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-questions'] })
      queryClient.invalidateQueries({ queryKey: ['survey-groups'] })
      queryClient.invalidateQueries({ queryKey: ['scripts', 'picker', 'questions'] })
    },
  })
}

export function useFollowUpSummary(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['followUp', campaignId, 'summary'],
    queryFn: () => followUpApi.getSummary(campaignId!).then(r => r.data),
    enabled: !!campaignId,
  })
}

export function useFollowUpItems(campaignId: string | undefined, params: FollowUpItemsParams) {
  return useQuery({
    queryKey: ['followUp', campaignId, 'items', params],
    queryFn: () => followUpApi.getItems(campaignId!, params).then(r => r.data),
    enabled: !!campaignId,
    placeholderData: keepPreviousData,
  })
}

export function useUpdateFollowUpItem(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, ...data }: UpdateFollowUpItemRequest & { itemId: string }) =>
      followUpApi.updateItem(campaignId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId] })
    },
  })
}

export function useCampaignViewSettings(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'viewSettings'],
    queryFn: () => followUpApi.getViewSettings(campaignId!).then(r => r.data),
    enabled: !!campaignId,
  })
}

export function useSaveCampaignViewSettings(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveCampaignViewSettingsRequest) => followUpApi.saveViewSettings(campaignId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'viewSettings'] })
    },
  })
}

// ── CF-1 P5: chase to vote ──
// Prepare and cut are 202 { jobId }: the caller polls the job and invalidates only on Succeeded
// (job-polling rules), so those mutations invalidate nothing. The call-list hand-off is synchronous.

export function useChaseSettings(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['followUp', campaignId, 'chase', 'settings'],
    queryFn: () => followUpApi.getChaseSettings(campaignId!).then(r => r.data),
    enabled: !!campaignId,
  })
}

export function useSaveChaseSettings(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveChaseSettingsRequest) =>
      followUpApi.saveChaseSettings(campaignId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'chase', 'settings'] })
      // The election and thresholds drive every chase count.
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'chase', 'summary'] })
    },
  })
}

export function useChaseSummary(campaignId: string | undefined, { includeWidened }: { includeWidened: boolean }) {
  return useQuery({
    queryKey: ['followUp', campaignId, 'chase', 'summary', { includeWidened }],
    queryFn: () => followUpApi.getChaseSummary(campaignId!, includeWidened).then(r => r.data),
    enabled: !!campaignId,
    placeholderData: keepPreviousData,
  })
}

export function usePrepareChase(campaignId: string) {
  return useMutation({
    mutationFn: (body: PrepareChaseRequest) => followUpApi.prepareChase(campaignId, body).then(r => r.data),
  })
}

export function useCutChaseBatch(campaignId: string) {
  return useMutation({
    mutationFn: ({ batchId, ...body }: CutChaseBatchRequest & { batchId: string }) =>
      followUpApi.cutChaseBatch(campaignId, batchId, body).then(r => r.data),
  })
}

export function useChaseCallList(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (batchId: string) => followUpApi.chaseCallList(campaignId, batchId).then(r => r.data),
    onSuccess: () => {
      // P4's call-batch keys (and the item queue state / tab counts its own writes refresh).
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'callBatches'] })
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'items'] })
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'summary'] })
    },
  })
}
